import type { Keyboard, KeyboardKey, Scale, ScaleStep } from './types';

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

/** Convert a ratio string like "3/2" or a number "1.5" into cents. Returns null on failure. */
export function parseRatioToCents(input: string): number | null {
  const s = input.trim();
  if (s === '') return null;

  // Try "a/b" ratio
  if (s.includes('/')) {
    const [a, b] = s.split('/').map((n) => parseFloat(n.trim()));
    if (Number.isNaN(a) || Number.isNaN(b) || b === 0 || a <= 0) return null;
    return 1200 * Math.log2(a / b);
  }

  // Try pure cents number
  const cents = parseFloat(s);
  if (!Number.isNaN(cents)) return cents;

  // Try decimal ratio
  const ratio = parseFloat(s);
  if (!Number.isNaN(ratio) && ratio > 0) {
    return 1200 * Math.log2(ratio);
  }
  return null;
}

/** Convert cents to a frequency multiplier: 2^(cents/1200). */
export function centsToRatio(cents: number): number {
  return Math.pow(2, cents / 1200);
}

/** Round to a reasonable number of decimal places. */
export function roundCents(c: number): number {
  return Math.round(c * 1000) / 1000;
}

// ---------------------------------------------------------------------------
// Frequency computation
// ---------------------------------------------------------------------------

/**
 * Compute the frequency of a given scale degree at a given period repetition.
 * freq = baseFrequency * 2^(periodCents * period / 1200) * 2^(stepCents / 1200)
 */
export function frequencyForNote(
  scale: Scale,
  degree: number,
  period: number,
): number {
  const stepCents = degreeToCents(scale, degree);
  const periodCents = scale.periodCents;
  const totalCents = periodCents * period + stepCents;
  return scale.baseFrequency * Math.pow(2, totalCents / 1200);
}

/**
 * Get the cents value for a scale degree (wraps within the period).
 * Degree 0 → 0, degree 1 → steps[0].cents, etc.
 * Degrees >= steps.length wrap into the next period.
 */
export function degreeToCents(scale: Scale, degree: number): number {
  const count = scale.steps.length + 1; // total degrees per period (incl. root)
  const wrapped = ((degree % count) + count) % count;
  if (wrapped === 0) return 0;
  return scale.steps[wrapped - 1].cents;
}

/** Number of distinct pitch classes per period (including root). */
export function degreesPerPeriod(scale: Scale): number {
  return scale.steps.length + 1;
}

// ---------------------------------------------------------------------------
// Scale generators
// ---------------------------------------------------------------------------

let idCounter = 0;
export function genId(prefix = 'id'): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

/** C4 = 261.6256 Hz — default base frequency (MIDI note 60). */
const C4_FREQ = 261.6256;

/** Generate an N-EDO scale (equal divisions of the octave, 1200 c). */
export function generateEDO(n: number, baseFrequency = C4_FREQ): Scale {
  const steps: ScaleStep[] = [];
  for (let i = 1; i < n; i++) {
    steps.push({ cents: roundCents((1200 / n) * i), label: `${i}/${n}` });
  }
  return {
    id: genId('scale'),
    name: `${n}-EDO`,
    description: `${n} equal divisions of the octave`,
    periodCents: 1200,
    steps,
    baseFrequency,
  };
}

/**
 * Generate N equal divisions of an arbitrary period.
 * E.g. Bohlen-Pierce = 13 equal divisions of the tritave (3/1 = 1901.955 c).
 */
export function generateEDX(
  n: number,
  periodCents: number,
  name: string,
  description: string,
  baseFrequency = C4_FREQ,
): Scale {
  const steps: ScaleStep[] = [];
  for (let i = 1; i < n; i++) {
    steps.push({ cents: roundCents((periodCents / n) * i), label: `${i}/${n}` });
  }
  return {
    id: genId('scale'),
    name,
    description,
    periodCents: roundCents(periodCents),
    steps,
    baseFrequency,
  };
}

/** Build a scale from a list of ratio strings. The period is the last ratio. */
export function generateRatios(
  ratios: string[],
  name: string,
  description: string,
  baseFrequency = C4_FREQ,
): Scale | null {
  const parsed = ratios.map(parseRatioToCents);
  if (parsed.some((c) => c === null)) return null;
  const cents = parsed as number[];
  if (cents.length < 1) return null;

  const periodCents = cents[cents.length - 1];
  const steps: ScaleStep[] = cents.slice(0, -1).map((c, i) => ({
    cents: roundCents(c),
    label: ratios[i],
    ratio: ratios[i],
  }));

  return {
    id: genId('scale'),
    name,
    description,
    periodCents: roundCents(periodCents),
    steps,
    baseFrequency,
  };
}

// ---------------------------------------------------------------------------
// Built-in scale presets
// ---------------------------------------------------------------------------

/** C4 = 261.6256 Hz — the base tone for all scales (MIDI note 60). */
const C4 = 261.6256;

export const SCALES: Scale[] = [
  generateEDO(12, C4),
  generateEDO(19, C4),
  generateEDO(24, C4),
  generateEDO(31, C4),
  generateEDO(53, C4),
  generateEDX(13, 1901.955, 'Bohlen-Pierce', '13 equal divisions of the tritave (3/1)', C4),
  generateRatios(
    ['9/8', '5/4', '4/3', '3/2', '5/3', '15/8', '2/1'],
    'Just Intonation (Major)',
    '5-limit just intonation major scale',
    C4,
  )!,
  generateRatios(
    ['16/15', '9/8', '6/5', '5/4', '4/3', '45/32', '3/2', '8/5', '5/3', '9/5', '15/8', '2/1'],
    '5-Limit Chromatic',
    '12-tone 5-limit just intonation',
    C4,
  )!,
  generateRatios(
    ['256/243', '9/8', '32/27', '81/64', '4/3', '729/512', '3/2', '128/81', '27/16', '16/9', '243/128', '2/1'],
    'Pythagorean',
    'Pythagorean tuning (3-limit)',
    C4,
  )!,
  generateEDX(7, 1901.955, 'BP Diatonic', 'Bohlen-Pierce diatonic mode (7 of 13)', C4),
];

// Mark as built-in and assign stable ids
SCALES.forEach((s, i) => {
  s.id = `builtin-scale-${i}`;
  s.builtIn = true;
});

// ---------------------------------------------------------------------------
// Keyboard generation
// ---------------------------------------------------------------------------

/** Standard 12-EDO note names (degree 0-11). */
const NOTE_NAMES_12 = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** For 12-EDO: which degrees are white keys (diatonic) vs black keys (accidentals). */
const WHITE_DEGREES_12 = new Set([0, 2, 4, 5, 7, 9, 11]);

/** QWERTY key labels for mapping computer keys to scale degrees. */
const QWERTY_KEYS = 'asdfghjkl;qwertyuiopzxcvbnm,.1234567890'.split('');

/**
 * Determine which degrees in a scale should be white vs black keys.
 * For 12-EDO this uses the standard piano pattern.
 * For other scales, uses a diatonic-like heuristic: maps the scale's degrees
 * to the closest standard 12-EDO white-key positions (0, 200, 400, 500, 700, 900, 1100 cents).
 */
function computeWhiteDegrees(scale: Scale): Set<number> {
  const dpp = degreesPerPeriod(scale);

  // 12-EDO: standard piano pattern
  if (dpp === 12 && Math.abs(scale.periodCents - 1200) < 0.01) {
    return WHITE_DEGREES_12;
  }

  // For octave-period scales, try to map to diatonic positions
  const whiteCents = [0, 200, 400, 500, 700, 900, 1100];
  const result = new Set<number>();
  result.add(0); // root is always white/accent

  if (Math.abs(scale.periodCents - 1200) < 1) {
    // Octave-period scale: find degrees closest to standard diatonic positions
    for (const targetCents of whiteCents.slice(1)) {
      let bestDegree = -1;
      let bestDist = Infinity;
      for (let d = 1; d < dpp; d++) {
        const stepCents = d === 0 ? 0 : scale.steps[d - 1].cents;
        const dist = Math.abs(stepCents - targetCents);
        if (dist < bestDist) {
          bestDist = dist;
          bestDegree = d;
        }
      }
      // Only mark as white if reasonably close (within 80 cents)
      if (bestDegree >= 0 && bestDist < 80) {
        result.add(bestDegree);
      }
    }
  }

  // If the heuristic didn't produce enough white keys, fall back to
  // marking every other degree starting from 0
  if (result.size < 3) {
    result.clear();
    for (let d = 0; d < dpp; d++) {
      if (d % 2 === 0) result.add(d);
    }
  }

  return result;
}

/** Generate a default note name for a degree in a scale. */
function defaultNoteName(scale: Scale, degree: number, period: number): string {
  const dpp = degreesPerPeriod(scale);

  // 12-EDO: standard note names with octave number
  if (dpp === 12 && Math.abs(scale.periodCents - 1200) < 0.01) {
    const wrapped = ((degree % dpp) + dpp) % dpp;
    return `${NOTE_NAMES_12[wrapped]}${4 + period}`;
  }

  // Other scales: use degree number
  return `${period}.${degree}`;
}

/**
 * Generate a keyboard from a scale.
 * Produces `numPeriods` worth of keys in pitch order (left to right).
 * Keys are assigned as white/black/accent based on the scale, with note names.
 * The rendering will place black keys overlaid on top of white keys (piano style).
 */
export function generateKeyboard(
  scale: Scale,
  options: { numPeriods?: number; name?: string } = {},
): Keyboard {
  const numPeriods = options.numPeriods ?? 3;
  const dpp = degreesPerPeriod(scale);
  const whiteDegrees = computeWhiteDegrees(scale);
  const keys: KeyboardKey[] = [];

  for (let p = 0; p < numPeriods; p++) {
    for (let d = 0; d < dpp; d++) {
      const globalIdx = p * dpp + d;
      const isRoot = d === 0;
      keys.push({
        id: `k-${p}-${d}`,
        degree: d,
        period: p,
        keyLabel: QWERTY_KEYS[globalIdx % QWERTY_KEYS.length],
        color: isRoot ? 'accent' : whiteDegrees.has(d) ? 'white' : 'black',
        noteName: defaultNoteName(scale, d, p),
      });
    }
  }

  return {
    id: genId('kb'),
    name: options.name ?? `${scale.name} Keyboard`,
    scale,
    keys,
  };
}

/** Built-in keyboards derived from built-in scales. */
export const KEYBOARDS: Keyboard[] = SCALES.map((s) => {
  const kb = generateKeyboard(s, { numPeriods: 3 });
  kb.id = `builtin-kb-${s.id}`;
  kb.builtIn = true;
  return kb;
});

// ---------------------------------------------------------------------------
// MIDI pitch helpers
// ---------------------------------------------------------------------------

/** Standard MIDI frequency: A4 (note 69) = 440 Hz, 12-TET. */
export function midiToFreq(note: number): number {
  return 440 * Math.pow(2, (note - 69) / 12);
}

/** Convert a frequency to (nearest MIDI note, cents offset from that note). */
export function freqToMidi(freq: number): { note: number; cents: number } {
  const exactNote = 69 + 12 * Math.log2(freq / 440);
  const note = Math.round(exactNote);
  const cents = Math.round((exactNote - note) * 100);
  return { note, cents };
}

/** Convert cents offset to a MIDI pitch-bend value in [-8192, 8192] for ±200c range. */
export function centsToPitchBend(cents: number, bendRangeCents = 200): number {
  const clamped = Math.max(-bendRangeCents, Math.min(bendRangeCents, cents));
  return Math.round((clamped / bendRangeCents) * 8192);
}

/** Split a 14-bit pitch bend into [lsb, msb] for MIDI wire format. */
export function pitchBendToBytes(bend: number): [number, number] {
  const v = Math.max(0, Math.min(16383, bend + 8192));
  return [v & 0x7f, (v >> 7) & 0x7f];
}
